import { createRouter, createWebHistory } from 'vue-router';

// Lazy-loaded routes — solo se descargan cuando el usuario navega a ellas
const Home    = () => import('@/components/HomeView.vue');
const Projects = () => import('@/components/ProjectsView.vue');
const About   = () => import('@/components/AboutView.vue');
const Blog    = () => import('@/components/BlogView.vue');
const Article = () => import('@/components/blog/ArticleView.vue');
const Contact = () => import('@/components/ContactView.vue');
const Error   = () => import('@/components/ErrorView.vue');
const Course  = () => import('@/components/courses/CourseView.vue');

const BASE_TITLE = 'Harry Bernol — Portfolio';
const BASE_DESC  = 'Portfolio de Harry Bernol, Backend Developer especializado en Java, Spring Boot y Vue.js.';
const BASE_URL   = 'https://mttechdev.com';
const BASE_IMAGE = `${BASE_URL}/images/og-image.png`;

const routes = [
  {
    path: '/',
    component: Home,
    name: 'home',
    meta: {
      title: 'Harry Bernol — Backend Developer',
      description: BASE_DESC
    }
  },
  {
    path: '/projects',
    component: Projects,
    name: 'projects',
    meta: {
      title: 'Proyectos — Harry Bernol',
      description: 'Proyectos de desarrollo de software: APIs, microservicios, aplicaciones web y más.'
    }
  },
  {
    path: '/about',
    component: About,
    name: 'about',
    meta: {
      title: 'Sobre mí — Harry Bernol',
      description: 'Experiencia, habilidades, publicaciones académicas y cursos impartidos.'
    }
  },
  {
    path: '/blog',
    component: Blog,
    name: 'blog',
    meta: {
      title: 'Blog — Harry Bernol',
      description: 'Artículos sobre desarrollo de software, Java, Spring Boot, Vue.js y más.'
    }
  },
  {
    path: '/blog/:slug',
    component: Article,
    name: 'article',
    meta: {
      title: 'Artículo — Harry Bernol',
      description: BASE_DESC
    }
  },
  {
    path: '/contact-me',
    component: Contact,
    name: 'contact',
    meta: {
      title: 'Contacto — Harry Bernol',
      description: '¿Tienes un proyecto en mente? Escríbeme y te respondo en menos de 24 horas.'
    }
  },
  {
    path: '/course/view',
    component: Course,
    name: 'course',
    meta: {
      title: 'Cursos — Harry Bernol',
      description: 'Cursos de desarrollo de software impartidos en plataformas online.'
    }
  },
  {
    path: '/error',
    component: Error,
    name: 'error',
    meta: { title: 'Error — Harry Bernol' }
  },
  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    component: Error,
    name: 'not-found',
    meta: { title: 'Página no encontrada — Harry Bernol' }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  }
});

// OJO: esto solo lo ven los clientes que ejecutan JavaScript. Los rastreadores de
// WhatsApp, LinkedIn, X y Facebook no lo hacen, y se quedan con lo que haya en
// public/index.html. Por eso alli estan las mismas etiquetas con valores por defecto:
// esto las afina por ruta, no las sustituye.
router.beforeEach((to, from, next) => {
  const title = to.meta.title || BASE_TITLE;
  const desc  = to.meta.description || BASE_DESC;
  const image = to.meta.ogImage || BASE_IMAGE;
  const url   = `${BASE_URL}${to.path}`;

  document.title = title;
  setMeta('name', 'description', desc);

  // Open Graph
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', desc);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', image);
  setMeta('property', 'og:type', to.name === 'article' ? 'article' : 'website');

  // Twitter Card
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', desc);
  setMeta('name', 'twitter:image', image);

  // Canonical: sin esto, cada ruta se anuncia como la portada y los buscadores
  // acaban indexando una sola pagina en vez de todas.
  setCanonical(url);

  // La pagina de error no debe indexarse.
  const noIndex = to.name === 'not-found' || to.name === 'error';
  setMeta('name', 'robots', noIndex ? 'noindex, follow' : 'index, follow, max-image-preview:large');

  next();
});

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default router;
